import ExportReport from "./ExportReport";
import AdminPermission from "components/admin/AdminPermission";
import {
  CashbackCampaign,
  CashbackSettings,
  cashbackKeys,
  excludeCashbackUser,
  fetchCashbackDashboard,
  fetchCashbackEarners,
  fetchCashbackSettings,
  saveCashbackSettings,
} from "api/admin/billCashback";
import AdminSidebar from "components/layout/AdminSidebar";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import Table from "components/ui/Table";
import VButton from "components/ui/VButton";
import { DateTime } from "luxon";
import { FormEvent, InputHTMLAttributes, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

const queryOptions = {
  staleTime: 60_000,
  retry: 1,
  refetchOnWindowFocus: false,
};
const money = (value: number | string) =>
  `₦${Number(value).toLocaleString("en-NG", { maximumFractionDigits: 2 })}`;
const panel = "rounded-xl border border-gray-200 bg-white p-5 md:p-6";
const zone = "Africa/Lagos";
const billTypes = [
  { value: "airtime", label: "Airtime" },
  { value: "mobile_data", label: "Mobile data" },
];
const networks = ["mtn", "airtel", "glo", "9mobile"];

const toForm = (campaign: CashbackCampaign | null) => ({
  enabled: campaign?.enabled ?? false,
  percent: String(Number(campaign?.rate ?? 0.02) * 100),
  totalBudget: String(campaign?.totalBudget ?? 500000),
  minimumPurchase: String(campaign?.minimumPurchase ?? 0),
  perUserCap:
    Number(campaign?.perUserCap ?? 0) > 0 ? String(campaign!.perUserCap) : "",
  startsAt: campaign
    ? DateTime.fromISO(campaign.startsAt)
        .setZone(zone)
        .toFormat("yyyy-MM-dd'T'HH:mm")
    : "",
  endsAt: campaign
    ? DateTime.fromISO(campaign.endsAt)
        .setZone(zone)
        .toFormat("yyyy-MM-dd'T'HH:mm")
    : "",
  eligibleBillTypes: campaign?.eligibleBillTypes ?? ["airtime", "mobile_data"],
  eligibleNetworks: campaign?.eligibleNetworks ?? networks,
  fraudPercent:
    campaign?.fraudBudgetShare == null
      ? ""
      : String(Number(campaign.fraudBudgetShare) * 100),
  fraudPurchaseCount:
    campaign?.fraudPurchaseCount == null
      ? ""
      : String(campaign.fraudPurchaseCount),
  fraudWindowSeconds:
    campaign?.fraudWindowSeconds == null
      ? ""
      : String(campaign.fraudWindowSeconds),
});

function campaignStatus(campaign: CashbackCampaign | null) {
  if (!campaign) return "Not configured";
  if (!campaign.enabled) return "Disabled";
  if (Date.now() > Date.parse(campaign.endsAt)) return "Ended";
  if (Date.now() < Date.parse(campaign.startsAt)) return "Scheduled";
  if (
    campaign.exhaustedAt ||
    Number(campaign.totalCredited) >= Number(campaign.totalBudget)
  )
    return "Budget exhausted";
  return "Enabled";
}

function Field({
  label,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return (
    <label className="block text-sm text-gray-700">
      <span className="block mb-2">{label}</span>
      <input
        {...props}
        className="w-full border-gray-300 rounded-lg disabled:bg-gray-100"
      />
    </label>
  );
}

function CampaignForm({ campaign }: { campaign: CashbackCampaign | null }) {
  // Initialize after loading; background refreshes must not erase unsaved edits.
  const [form, setForm] = useState(() => toForm(campaign));
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const client = useQueryClient();
  const save = useMutation(saveCashbackSettings, {
    onSuccess: (updated) => {
      setForm(toForm(updated));
      setSaved(true);
      client.setQueryData(cashbackKeys.settings, updated);
      void client.invalidateQueries(cashbackKeys.dashboard);
    },
    onError: () =>
      setError(
        "Could not save the campaign. Your changes are still here; please try again.",
      ),
  });
  const change = <K extends keyof typeof form>(
    key: K,
    value: (typeof form)[K],
  ) => {
    setSaved(false);
    setError("");
    setForm((current) => ({ ...current, [key]: value }));
  };
  const toggle = (
    key: "eligibleBillTypes" | "eligibleNetworks",
    value: string,
  ) => {
    change(
      key,
      form[key].includes(value)
        ? form[key].filter((item) => item !== value)
        : [...form[key], value],
    );
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setSaved(false);
    setError("");
    const start = DateTime.fromISO(form.startsAt, { zone });
    const end = DateTime.fromISO(form.endsAt, { zone });
    if (!start.isValid || !end.isValid || end.toMillis() <= start.toMillis()) {
      return setError("End date must be after the start date.");
    }
    if (!form.eligibleBillTypes.length || !form.eligibleNetworks.length) {
      return setError("Choose at least one bill type and one network.");
    }
    if (Number(form.totalBudget) < Number(campaign?.totalCredited ?? 0)) {
      return setError(
        "The budget cannot be less than cashback already credited.",
      );
    }
    if (Boolean(form.fraudPurchaseCount) !== Boolean(form.fraudWindowSeconds)) {
      return setError(
        "Set both a purchase count and a time window for the frequency alert.",
      );
    }
    const settings: CashbackSettings = {
      enabled: form.enabled,
      rate: Number(form.percent) / 100,
      totalBudget: Number(form.totalBudget),
      minimumPurchase: Number(form.minimumPurchase),
      perUserCap: form.perUserCap === "" ? null : Number(form.perUserCap),
      startsAt: start.toUTC().toISO()!,
      endsAt: end.toUTC().toISO()!,
      eligibleBillTypes: form.eligibleBillTypes,
      eligibleNetworks: form.eligibleNetworks,
      roundingRule: campaign?.roundingRule ?? "round_half_up",
      fraudBudgetShare:
        form.fraudPercent === "" ? null : Number(form.fraudPercent) / 100,
      fraudPurchaseCount:
        form.fraudPurchaseCount === "" ? null : Number(form.fraudPurchaseCount),
      fraudWindowSeconds:
        form.fraudWindowSeconds === "" ? null : Number(form.fraudWindowSeconds),
    };
    save.mutate(settings);
  };

  return (
    <form onSubmit={submit} className={panel}>
      <fieldset disabled={save.isLoading}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-medium">Campaign settings</h2>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => change("enabled", e.target.checked)}
            />
            Enable cashback
          </label>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          Changes apply when you save. Cashback goes to the normal Naira wallet
          after bill success.
        </p>
        <div className="grid gap-5 mt-6 sm:grid-cols-2">
          <Field
            label="Cashback rate (%)"
            type="number"
            required
            min="0.0001"
            max="99.9999"
            step="0.0001"
            value={form.percent}
            onChange={(e) => change("percent", e.target.value)}
          />
          <Field
            label="Total budget (₦)"
            type="number"
            required
            min="0.01"
            step="0.01"
            value={form.totalBudget}
            onChange={(e) => change("totalBudget", e.target.value)}
          />
          <Field
            label="Minimum purchase (₦)"
            type="number"
            required
            min="0"
            step="0.01"
            value={form.minimumPurchase}
            onChange={(e) => change("minimumPurchase", e.target.value)}
          />
          <Field
            label="Per-user cap (₦, optional)"
            type="number"
            min="0.01"
            step="0.01"
            placeholder="No cap"
            value={form.perUserCap}
            onChange={(e) => change("perUserCap", e.target.value)}
          />
          <Field
            label="Starts at (Lagos time)"
            type="datetime-local"
            required
            value={form.startsAt}
            onChange={(e) => change("startsAt", e.target.value)}
          />
          <Field
            label="Ends at (Lagos time)"
            type="datetime-local"
            required
            value={form.endsAt}
            onChange={(e) => change("endsAt", e.target.value)}
          />
        </div>
        <p className="mt-3 text-xs text-gray-500">
          Saving keeps existing campaign spending and exclusions. Increase the
          budget to reopen an exhausted campaign.
        </p>
        <p className="mt-2 text-xs text-gray-500">
          The mobile promotion artwork currently advertises 2%. Update it before
          launching a different rate.
        </p>
        <div className="grid gap-5 mt-6 sm:grid-cols-2">
          <fieldset>
            <legend className="mb-3 text-sm font-medium">Eligible bills</legend>
            {billTypes.map(({ value, label }) => (
              <label
                key={value}
                className="flex items-center gap-2 mb-2 text-sm"
              >
                <input
                  type="checkbox"
                  checked={form.eligibleBillTypes.includes(value)}
                  onChange={() => toggle("eligibleBillTypes", value)}
                />
                {label}
              </label>
            ))}
          </fieldset>
          <fieldset>
            <legend className="mb-3 text-sm font-medium">
              Eligible networks
            </legend>
            <div className="grid grid-cols-2 gap-2">
              {networks.map((network) => (
                <label
                  key={network}
                  className="flex items-center gap-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={form.eligibleNetworks.includes(network)}
                    onChange={() => toggle("eligibleNetworks", network)}
                  />
                  {network === "9mobile" ? network : network.toUpperCase()}
                </label>
              ))}
            </div>
          </fieldset>
        </div>
        <details className="pt-4 mt-6 border-t border-gray-100">
          <summary className="text-sm font-medium cursor-pointer">
            Optional fraud alerts
          </summary>
          <p className="mt-2 text-sm text-gray-500">
            Alerts notify your team in Slack. They do not block purchases.
          </p>
          <div className="grid gap-4 mt-4 sm:grid-cols-3">
            <Field
              label="User share of budget (%)"
              type="number"
              min="0.0001"
              max="99.9999"
              step="0.0001"
              value={form.fraudPercent}
              onChange={(e) => change("fraudPercent", e.target.value)}
            />
            <Field
              label="Purchases in window"
              type="number"
              min="1"
              step="1"
              value={form.fraudPurchaseCount}
              onChange={(e) => change("fraudPurchaseCount", e.target.value)}
            />
            <Field
              label="Window (seconds)"
              type="number"
              min="1"
              step="1"
              value={form.fraudWindowSeconds}
              onChange={(e) => change("fraudWindowSeconds", e.target.value)}
            />
          </div>
        </details>
        {error && (
          <p role="alert" className="mt-4 text-sm text-red-700">
            {error}
          </p>
        )}
        {saved && (
          <p role="status" className="mt-4 text-sm text-green-700">
            Campaign saved.
          </p>
        )}
        <div className="mt-6">
          <VButton
            className="text-white"
            status={save.isLoading ? "pending" : "idle"}
          >
            Save campaign
          </VButton>
        </div>
      </fieldset>
    </form>
  );
}

function ExcludeUser() {
  const [userId, setUserId] = useState("");
  const exclude = useMutation(excludeCashbackUser);
  return (
    <form
      className={panel}
      onSubmit={(event) => {
        event.preventDefault();
        exclude.mutate(Number(userId));
      }}
    >
      <h2 className="text-lg font-medium">Exclude a user</h2>
      <p className="mt-2 text-sm text-gray-500">
        Stops future cashback in this campaign. Existing credits remain
        unchanged. Exclusions cannot be removed here.
      </p>
      <fieldset
        disabled={exclude.isLoading}
        className="flex flex-wrap items-end gap-3 mt-4"
      >
        <Field
          label="User ID"
          type="number"
          required
          min="1"
          step="1"
          value={userId}
          onChange={(e) => {
            setUserId(e.target.value);
            exclude.reset();
          }}
        />
        <VButton
          className="text-white"
          status={exclude.isLoading ? "pending" : "idle"}
        >
          Exclude user
        </VButton>
      </fieldset>
      {exclude.isError && (
        <p role="alert" className="mt-3 text-sm text-red-700">
          Could not exclude the user. Please try again.
        </p>
      )}
      {exclude.isSuccess && (
        <p role="status" className="mt-3 text-sm text-green-700">
          User {exclude.variables} is excluded from this campaign.
        </p>
      )}
    </form>
  );
}

export default function Cashback() {
  const [showEarners, setShowEarners] = useState(false);
  // Independent reads run in parallel. No polling or report scans on page load.
  const settings = useQuery(
    cashbackKeys.settings,
    fetchCashbackSettings,
    queryOptions,
  );
  const dashboard = useQuery(
    cashbackKeys.dashboard,
    fetchCashbackDashboard,
    queryOptions,
  );
  const earners = useQuery(cashbackKeys.earners, fetchCashbackEarners, {
    ...queryOptions,
    enabled: showEarners,
  });
  const campaign = settings.data ?? null;
  return (
    <Container className="flex-col md:flex-row">
      <AdminSidebar />
      <Main className="min-w-0 pb-10 text-left">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl">Bill cashback</h1>
              <p className="mt-2 text-sm text-gray-500">
                Manage airtime and data cashback, campaign spending and
                eligibility.
              </p>
            </div>
            <AdminPermission permission="billCashback.campaigns.export">
              <ExportReport />
            </AdminPermission>
            {!settings.isLoading && !settings.isError && (
              <span className="px-4 py-2 text-sm text-orange-800 rounded-full bg-orange-50">
                {campaignStatus(campaign)}
              </span>
            )}
          </div>
          {settings.isLoading ? (
            <p role="status" className="mt-8">
              Loading cashback settings…
            </p>
          ) : settings.isError && settings.data === undefined ? (
            <div role="alert" className="p-4 mt-8 rounded-lg bg-red-50">
              Could not load cashback settings.{" "}
              <button
                className="underline"
                onClick={() => void settings.refetch()}
              >
                Retry settings
              </button>
            </div>
          ) : (
            <>
              {settings.isError && (
                <p role="alert" className="mt-6 text-sm text-red-700">
                  Could not refresh settings. Your edits are preserved.{" "}
                  <button
                    className="underline"
                    onClick={() => void settings.refetch()}
                  >
                    Retry settings
                  </button>
                </p>
              )}
              {!campaign && (
                <p className="p-4 mt-6 text-sm rounded-lg bg-orange-50">
                  No campaign yet. Set the dates and budget below, then save to
                  create one. Cashback starts disabled by default.
                </p>
              )}
              <section aria-label="Campaign summary" className="my-6">
                {dashboard.isError ? (
                  <p role="alert">
                    Could not load campaign totals.{" "}
                    <button
                      className="underline"
                      onClick={() => void dashboard.refetch()}
                    >
                      Retry totals
                    </button>
                  </p>
                ) : dashboard.isLoading ? (
                  <p role="status">Loading campaign totals…</p>
                ) : (
                  dashboard.data && (
                    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                      {[
                        [
                          "Cashback paid (net)",
                          money(dashboard.data.totalCashbackPaid),
                        ],
                        [
                          "Budget remaining",
                          money(dashboard.data.budgetRemaining),
                        ],
                        [
                          "Users rewarded",
                          dashboard.data.usersEarned.toLocaleString(),
                        ],
                        [
                          "Rewarded purchases",
                          dashboard.data.qualifyingPurchases.toLocaleString(),
                        ],
                      ].map(([label, value]) => (
                        <div
                          className="p-4 border border-gray-200 rounded-xl"
                          key={label}
                        >
                          <p className="text-xs text-gray-500">{label}</p>
                          <p className="mt-2 text-xl font-medium">{value}</p>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </section>
              <AdminPermission permission="billCashback.campaigns.edit">
                <CampaignForm campaign={campaign} />
              </AdminPermission>
              {campaign && (
                <div className="grid items-start gap-6 mt-6 xl:grid-cols-2">
                  <details
                    className={panel}
                    onToggle={(e) => setShowEarners(e.currentTarget.open)}
                  >
                    <summary className="text-lg font-medium cursor-pointer">
                      Top 20 earners
                    </summary>
                    {showEarners && (
                      <div className="mt-4 overflow-x-auto">
                        <Table
                          header={["User", "Cashback"]}
                          isLoading={earners.isLoading}
                          errorMessage={
                            earners.isError
                              ? "Could not load top earners."
                              : undefined
                          }
                          onRetry={() => void earners.refetch()}
                          emptyMessage="No cashback has been credited yet."
                          data={(earners.data ?? []).map((earner) => [
                            `User ${earner.userId}`,
                            money(earner.totalCashback),
                            undefined,
                          ])}
                        />
                      </div>
                    )}
                  </details>
                  <AdminPermission permission="billCashback.campaigns.edit">
                    <ExcludeUser />
                  </AdminPermission>
                </div>
              )}
            </>
          )}
        </div>
      </Main>
    </Container>
  );
}
